import React from 'react'
import styled from '@emotion/styled'
import {
  Grid,
  Flex,
  Text,
  theme,
  TruncatedText,
} from '@mindfulchefuk/design-system'
import { macroRounding } from '@mindfulchefuk/utils/macroRounding'
import { RecipeWithQuantity } from '@mindfulchefuk/features/Recipes/interfaces'
import toCurrency from '@mindfulchefuk/utils/toCurrency'
import plural from '@mindfulchefuk/utils/plural'
import { RecipeRating } from '@mindfulchefuk/features/Recipes/components/RecipeCardWithClaims/RecipeCardWithClaimsStats/RecipeRating/RecipeRating'

type TRecipeCardWithClaimsStats = {
  recipe: RecipeWithQuantity
  showPrice: boolean
}

const Dot = styled('div')`
  width: ${theme.space[2]};
  height: ${theme.space[2]};
  border-radius: 100%;
  background-color: ${theme.colors['pepper-500']};
  margin: ${theme.space[0]} ${theme.space[8]};
`

const GridWithGap = styled(Grid)`
  gap: ${theme.space[4]};
`

const StyledFlex = styled(Flex)`
  text-overflow: ellipsis;
`

export const recipeRoundelRatingCopy = (rating: number) => {
  const tasty = rating >= 4 ? ' a tasty' : ''
  const stars = plural('star', 'stars')(rating)

  return `You rated this recipe${tasty} ${rating} ${stars}`
}

export const RecipeCardWithClaimsStats = ({
  recipe,
  showPrice,
}: TRecipeCardWithClaimsStats) => {
  const {
    cookingTime,
    feedback,
    foodGroup,
    macros,
    price,
    pricePerPerson,
    id,
    title,
  } = recipe

  const isVeganOrVeggie = foodGroup === 'Dairy' || foodGroup === 'Vegan'
  const portionPrice = toCurrency(pricePerPerson, { style: 'currency' })
  const hasThreeLines = showPrice || feedback?.rating

  return (
    <GridWithGap
      gridTemplateRows={`repeat(${hasThreeLines ? 3 : 2}, 22px)`}
      whiteSpace="nowrap"
    >
      <StyledFlex alignItems="center" overflow="hidden">
        {isVeganOrVeggie && (
          <>
            <Text color="broccoli" variant="body-md-500">
              {foodGroup === 'Dairy' ? 'Veggie' : 'Vegan'}
            </Text>
            <Dot />
          </>
        )}

        <Text
          aria-label={`Cooking time: ${cookingTime} min`}
          color="pepper-500"
          variant="body-md"
        >
          {`${cookingTime} min`}
        </Text>
        <Dot />

        <Text color="pepper-500" variant="body-md">
          {`${macroRounding.energy(
            macros.macrosPerPerson.calories as number
          )} kcal`}
        </Text>
      </StyledFlex>

      <StyledFlex alignItems="center" overflow="hidden">
        <Text color="pepper-500" variant="body-md">
          {`${macroRounding.sugar(
            macros.macrosPerPerson.carbohydrates as number
          )}g carbs`}
        </Text>
        <Dot />

        <Text color="pepper-500" variant="body-md">
          {`${macroRounding.fat(macros.macrosPerPerson.fat as number)}g fat`}
        </Text>
        <Dot />
        <TruncatedText color="pepper-500" variant="body-md">
          {`${macroRounding.protein(
            macros.macrosPerPerson.protein as number
          )}g protein`}
        </TruncatedText>
      </StyledFlex>

      {hasThreeLines && (
        <Flex alignItems="center">
          {showPrice && (
            <Text color="pepper-500" variant="body-md">
              {`${toCurrency(price, {
                style: 'decimal',
              })} (${portionPrice} pp)`}
            </Text>
          )}

          {feedback && (
            <>
              <Dot />
              <RecipeRating
                feedback={feedback}
                productId={id}
                productName={title}
              />
            </>
          )}
        </Flex>
      )}
    </GridWithGap>
  )
}
